import "@arco-design/web-react/es/Breadcrumb/style";
import _Breadcrumb from "@arco-design/web-react/es/Breadcrumb";
import _IconMenuFold from "@arco-design/web-react/icon/react-icon/IconMenuFold";
import _IconMenuUnfold from "@arco-design/web-react/icon/react-icon/IconMenuUnfold";
import "@arco-design/web-react/es/Spin/style";
import _Spin from "@arco-design/web-react/es/Spin";
import "@arco-design/web-react/es/Menu/style";
import _Menu from "@arco-design/web-react/es/Menu";
import "@arco-design/web-react/es/Layout/style";
import _Layout from "@arco-design/web-react/es/Layout";
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import cs from 'classnames';
import NProgress from 'nprogress';
import Navbar from '@/components/NavBar';
import Footer from '@/components/Footer';
import useLocale from '@/utils/useLocale';
import getUrlParams from '@/utils/getUrlParams';
import { getPage, getRootMenu, useActiveRoute, useResolvedRoutes } from '@/routes';
import styles from '@/style/layout.module.less';
const {
  Sider,
  Content
} = _Layout;
const MenuItem = _Menu.Item;
const SubMenu = _Menu.SubMenu;
const NAVBAR_HEIGHT = 60;

function renderMenuTree(nodes, locale) {
  return nodes.map(node => {
    const Icon = node.menu?.icon;
    const title = /*#__PURE__*/React.createElement(React.Fragment, null, Icon ?
    /*#__PURE__*/
    // arco-icon: 自定义 svg (svgr 组件) 带死的 width/height, 靠这个类
    // 才能跟内置 icon 一样按 font-size 缩放、继承 currentColor
    React.createElement(Icon, {
      className: cs('arco-icon', styles.icon)
    }) : /*#__PURE__*/React.createElement("div", {
      className: styles['icon-empty']
    }), locale[node.name] || node.name, " ", node.menu?.badge);

    if (node.children?.length) {
      return /*#__PURE__*/React.createElement(SubMenu, {
        key: node.key,
        title: title
      }, renderMenuTree(node.children, locale));
    }

    return /*#__PURE__*/React.createElement(MenuItem, {
      key: node.key
    }, title);
  });
}

function flattenMenu(nodes, acc = []) {
  nodes.forEach(node => {
    acc.push(node);

    if (node.children?.length) {
      flattenMenu(node.children, acc);
    }
  });
  return acc;
}

export default function BaseLayout({
  match,
  children
}) {
  const history = useHistory();
  const locale = useLocale();
  const urlParams = getUrlParams();
  const activeRoute = useActiveRoute();
  const routes = useResolvedRoutes();
  const {
    settings,
    userLoading
  } = useSelector(state => state); // 命中路由时用它的作用域菜单；未命中（如 403 兜底页）时回退到根菜单

  const menuNodes = useMemo(() => match?.menu ?? getRootMenu(routes), [match, routes]);
  const menuIndex = useMemo(() => new Map(flattenMenu(menuNodes).map(node => [node.key, node])), [menuNodes]);
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const selectedKeys = match ? [match.route.node.key] : []; // 路由变化时自动展开当前节点的父级 SubMenu（menuContainer 本身不展开）

  useEffect(() => {
    if (!match) {
      return;
    }

    const parentKeys = match.route.parents.filter(node => node.children?.length && !node.menuContainer).map(node => node.key);
    setOpenKeys(prev => Array.from(new Set([...prev, ...parentKeys])));
  }, [match]);

  function onClickMenuItem(key) {
    const node = menuIndex.get(key);

    if (!node || node.url === '#') {
      return;
    }

    if (node.component) {
      getPage({
        key: node.key,
        component: node.component
      }).preload?.();
    }

    NProgress.start();
    history.push(node.url);
    NProgress.done();
  }

  const menuWidth = collapsed ? 48 : settings.menuWidth;
  const showNavbar = settings.navbar && urlParams.navbar !== false;
  const showMenu = settings.menu && urlParams.menu !== false;
  const showFooter = settings.footer && urlParams.footer !== false;
  const paddingStyle = { ...(showMenu ? {
      paddingLeft: menuWidth
    } : {}),
    ...(showNavbar ? {
      paddingTop: NAVBAR_HEIGHT
    } : {})
  };
  return /*#__PURE__*/React.createElement(_Layout, {
    className: styles.layout
  }, /*#__PURE__*/React.createElement("div", {
    className: cs(styles['layout-navbar'], {
      [styles['layout-navbar-hidden']]: !showNavbar
    })
  }, /*#__PURE__*/React.createElement(Navbar, {
    show: showNavbar
  })), userLoading ? /*#__PURE__*/React.createElement(_Spin, {
    className: styles.spin
  }) : /*#__PURE__*/React.createElement(_Layout, null, showMenu && /*#__PURE__*/React.createElement(Sider, {
    className: styles['layout-sider'],
    width: menuWidth,
    collapsed: collapsed,
    onCollapse: setCollapsed,
    trigger: null,
    collapsible: true,
    breakpoint: "xl",
    style: showNavbar ? {
      paddingTop: NAVBAR_HEIGHT
    } : {}
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['menu-wrapper']
  }, /*#__PURE__*/React.createElement(_Menu, {
    collapse: collapsed,
    onClickMenuItem: onClickMenuItem,
    selectedKeys: selectedKeys,
    openKeys: openKeys,
    onClickSubMenu: (_, keys) => setOpenKeys(keys)
  }, renderMenuTree(menuNodes, locale))), /*#__PURE__*/React.createElement("div", {
    className: styles['collapse-btn'],
    onClick: () => setCollapsed(value => !value)
  }, collapsed ? /*#__PURE__*/React.createElement(_IconMenuUnfold, null) : /*#__PURE__*/React.createElement(_IconMenuFold, null))), /*#__PURE__*/React.createElement(_Layout, {
    className: styles['layout-content'],
    style: paddingStyle
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['layout-content-wrapper']
  }, !!match?.breadcrumb.length && /*#__PURE__*/React.createElement("div", {
    className: styles['layout-breadcrumb']
  }, /*#__PURE__*/React.createElement(_Breadcrumb, null, match.breadcrumb.map(node => /*#__PURE__*/React.createElement(_Breadcrumb.Item, {
    key: node.key
  }, /*#__PURE__*/React.createElement("a", {
    onClick: () => {
      if (node.url !== activeRoute?.url) {
        history.push(node.url);
      }
    }
  }, locale[node.name] || node.name))))), /*#__PURE__*/React.createElement(Content, null, children)), showFooter && /*#__PURE__*/React.createElement(Footer, null))));
}